import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { ClipboardList, Loader2, Activity, Slack } from 'lucide-react';
import { format } from 'date-fns';

const DigestCard = ({
  digest, setDigest,
  runningDigest, runDigestNow,
  savingDigest, saveDigest,
}) => {
  if (!digest) return null;
  return (
    <Card className="border-[#E5E7EB]" data-testid="digest-card">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <CardTitle className="text-sm flex items-center gap-2">
            <ClipboardList className="w-4 h-4 text-[#002FA7]" />Weekly Automation Health Digest
            {digest.enabled ? (
              <Badge className="text-[10px] bg-[#34C759]/15 text-[#34C759] border-0" data-testid="digest-armed-badge">Armed</Badge>
            ) : (
              <Badge variant="outline" className="text-[10px] text-[#9CA3AF]" data-testid="digest-disabled-badge">Disabled</Badge>
            )}
          </CardTitle>
          <Button
            size="sm" variant="outline" onClick={runDigestNow}
            disabled={runningDigest || (!digest.recipient_email && !digest.slack_webhook_url)}
            data-testid="digest-run-now-btn"
          >
            {runningDigest ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Activity className="w-3 h-3 mr-1" />}
            Run Now
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        <p className="text-[11px] text-[#4B5563] mb-3">
          Analyzes the last 7 days of all scheduled + manually-triggered automation runs and emails + Slacks the admin a digest with regressions (green→red flips), duration outliers, top error buckets, and a per-automation scoreboard. Drill down anytime at <code className="text-[10px] bg-[#F3F4F6] px-1 rounded">/admin/automations/runs</code>.
        </p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-3">
          <div>
            <Label className="text-[10px] text-[#4B5563]">Day of week</Label>
            <select
              className="w-full mt-1 border border-[#E5E7EB] rounded px-2 py-1.5 text-sm"
              value={digest.day_of_week || 'mon'}
              onChange={e => setDigest({ ...digest, day_of_week: e.target.value })}
              data-testid="digest-day-select"
            >
              {['mon','tue','wed','thu','fri','sat','sun'].map(d => (
                <option key={d} value={d}>{d.toUpperCase()}</option>
              ))}
            </select>
          </div>
          <div>
            <Label className="text-[10px] text-[#4B5563]">Hour (UTC)</Label>
            <select
              className="w-full mt-1 border border-[#E5E7EB] rounded px-2 py-1.5 text-sm"
              value={digest.hour ?? 8}
              onChange={e => setDigest({ ...digest, hour: Number(e.target.value) })}
              data-testid="digest-hour-select"
            >
              {Array.from({ length: 24 }).map((_, h) => (
                <option key={h} value={h}>{String(h).padStart(2, '0')}:00</option>
              ))}
            </select>
          </div>
          <div>
            <Label className="text-[10px] text-[#4B5563]">Recipient email</Label>
            <Input
              value={digest.recipient_email || ''}
              onChange={e => setDigest({ ...digest, recipient_email: e.target.value })}
              placeholder="ops@yourdomain.com"
              className="mt-1"
              data-testid="digest-recipient-input"
            />
          </div>
        </div>
        <div className="mb-3" data-testid="digest-slack-row">
          <Label className="text-[10px] text-[#4B5563] flex items-center gap-1">
            <Slack className="w-3 h-3" />Slack webhook URL <span className="text-[#9CA3AF]">(optional — posts a compact summary)</span>
          </Label>
          <Input
            value={digest.slack_webhook_url || ''}
            onChange={e => setDigest({ ...digest, slack_webhook_url: e.target.value })}
            placeholder="https://hooks.slack.com/services/T000/B000/xxxxxxxx"
            className="flex-1 font-mono text-xs mt-1"
            data-testid="digest-slack-input"
          />
          <p className="text-[10px] text-[#9CA3AF] mt-1">
            Tip: you can reuse the same webhook as the Readiness Watchdog above.
          </p>
        </div>

        <div className="flex items-center justify-between flex-wrap gap-2 pt-2 border-t border-[#F3F4F6]">
          <label className="flex items-center gap-2 text-xs text-[#0A0A0A] cursor-pointer select-none">
            <input
              type="checkbox"
              checked={!!digest.enabled}
              onChange={e => setDigest({ ...digest, enabled: e.target.checked })}
              data-testid="digest-enabled-toggle"
            />
            Arm digest (run weekly)
          </label>
          <Button
            size="sm" onClick={saveDigest} disabled={savingDigest}
            className="bg-[#002FA7] hover:bg-[#00227A]"
            data-testid="digest-save-btn"
          >
            {savingDigest ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : null}Save
          </Button>
        </div>
        {digest.last_run_at && (
          <div className="mt-3 p-2 bg-[#FAFAFA] rounded text-[11px] text-[#4B5563]" data-testid="digest-last-run">
            Last run <strong>{format(new Date(digest.last_run_at), 'MMM d, HH:mm')}</strong> UTC —{' '}
            <strong className="text-[#0A0A0A]">{digest.last_run_total_runs || 0}</strong> runs analyzed,{' '}
            <strong className={digest.last_run_regressions > 0 ? 'text-[#FF3B30]' : 'text-[#34C759]'}>
              {digest.last_run_regressions || 0}
            </strong> regression{digest.last_run_regressions === 1 ? '' : 's'}
            {(digest.last_alerted_email || digest.last_alerted_slack) && (
              <span className="text-[#002FA7]"> · sent via
                {digest.last_alerted_email && ' email'}
                {digest.last_alerted_email && digest.last_alerted_slack && ' +'}
                {digest.last_alerted_slack && ' slack'}
              </span>
            )}
            {digest.last_alert_error && (
              <span className="text-[#FF3B30]"> · error: {digest.last_alert_error.substring(0, 140)}</span>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default DigestCard;
